import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Upload,
} from 'antd';
import ImgCrop from 'antd-img-crop';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import toast from 'react-hot-toast';

import {RqAddProductFormData} from '../../../../reactQuery/apis';

const NewProductScreen = ({
  brandOptions,
  categoryOptions,
  promoCodeOptions,
  isModalVisible,
  handleCancel,
  form,
  token,
}) => {
  const [featuresOptions, setFeaturesOptions] = useState([]);
  const [isFeaturesDisabled, setIsFeaturesDisabled] = useState(true);

  const handleCategoryChange = value => {
    form.setFieldsValue({features: []});
    const category = categoryOptions.find(c => c.value === value);

    if (category?.features?.features?.length) {
      setFeaturesOptions(
        category.features.features.map(f => ({label: f, value: f})),
      );
      setIsFeaturesDisabled(false);
    } else {
      setFeaturesOptions([]);
      setIsFeaturesDisabled(true);
    }
  };

  const addProductMutation = RqAddProductFormData({
    onSuccess: () => {
      toast.success('Product created successfully');
      form.resetFields();
      handleCancel();
    },
    onError: err => {
      const backendMessage =
    err?.response?.data?.message || 
    err?.message ||
    'Failed to create product';

      toast.error(backendMessage);
    },
  });

  const onFinish = async values => {
    try {
      const formData = new FormData();

      const command = {
        productName: values.productName,
        description: values.description,
        price: values.price,
        productType: 'HANDBAG',
        status: values.status,
        discount: values.discount || 0,
        promoCode: values.promoCode || '',
        category: values.category,
        brand: values.brand,
        eanCode: values.eanCode,
        barcode: values.eanCode,
        features: values.features || [],
        variants: values.variants.map(v => ({
          color: v.color.trim().toLowerCase(),
          sizes: v.sizes.map(s => ({
            size: s.size,
            quantity: s.quantity,
            extraPrice: s.extraPrice || 0,
          })),
        })),
      };

      formData.append('command', JSON.stringify(command));

      values.variants.forEach(variant => {
        const color = variant.color.trim().toLowerCase();
        if (!Array.isArray(variant.images)) {return;}

        variant.images.forEach((fileWrapper, index) => {
          const file = fileWrapper?.originFileObj;
          if (!file) {return;}

          const ext = file.name.split('.').pop();
          const renamedFile = new File(
            [file],
            `${color}_${index + 1}.${ext}`,
            {type: file.type},
          );

          formData.append('files', renamedFile);
        });
      });

      await addProductMutation.mutateAsync({
        token,
        formData,
      });

    } catch (err) {
      console.error(err);
    }
  };

  const normFile = e => (Array.isArray(e) ? e : e?.fileList ?? []);

  return (
    <Modal
      title={'Add New Product'}
      open={isModalVisible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      width={900}
      destroyOnClose
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={onFinish}
        initialValues={{
          status: 'ACTIVE',
          variants: [
            {
              color: '',
              images: [],
              sizes: [{size: '', quantity: 1, extraPrice: 0}],
            },
          ],
        }}
      >
        {/* BASIC INFO */}
        <div className={'flex gap-4'}>
          <Form.Item
            name={'eanCode'}
            label={'EAN Code'}
            rules={[
              {required: true, message: 'Please enter EAN code'},
              {pattern: /^\d+$/, message: 'EAN must contain only numbers'},
              {min: 8, message: 'EAN must be at least 8 digits'},
              {max: 13, message: 'EAN must be at most 13 digits'},
            ]}
            className={'w-1/4'}
          >
            <Input
              maxLength={13}
              onKeyPress={e => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name={'productName'}
            label={'Product Name'}
            rules={[{required: true}]}
            className={'w-3/4'}
          >
            <Input />
          </Form.Item>
        </div>

        <Form.Item name={'description'} label={'Description'} rules={[{required: true}]}>
          <Input.TextArea rows={3} />
        </Form.Item>

        {/* CATEGORY / BRAND */}
        <div className={'flex md:flex-row flex-col md:gap-4'}>
          <Form.Item name={'status'} label={'Status'} className={'md:w-1/3'}>
            <Select
              options={[
                {label: 'Active', value: 'ACTIVE'},
                {label: 'Inactive', value: 'INACTIVE'},
              ]}
            />
          </Form.Item>
          <Form.Item name={'category'} label={'Category'} rules={[{required: true}]} className={'md:w-1/3'}>
            <Select options={categoryOptions} onChange={handleCategoryChange} />
          </Form.Item>

          <Form.Item name={'brand'} label={'Brand'} rules={[{required: true}]} className={'md:w-1/3'}>
            <Select options={brandOptions} />
          </Form.Item>
        </div>

        <Form.Item name={'features'} label={'Features'}>
          <Select
            mode={'multiple'}
            options={featuresOptions}
            disabled={isFeaturesDisabled}
          />
        </Form.Item>

        {/* PRICE */}
        <div className={'flex gap-4'}>
          <Form.Item
            name={'price'}
            label={'Base Price'}
            rules={[
              {required: true, message: 'Please enter price'},
              {pattern: /^\d+$/, message: 'price must contain only numbers'},
            ]}
            className={'w-1/3'}
          >
            <Input
              maxLength={7}
              onKeyPress={e => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>

          <Form.Item name={'discount'} label={'Discount (%)'} className={'w-1/3'}>
            <InputNumber min={0} max={100} className={'w-full'} />
          </Form.Item>

          <Form.Item name={'promoCode'} label={'Promo Code'} className={'w-1/3'}>
            <Select allowClear options={promoCodeOptions} />
          </Form.Item>
        </div>

        {/* VARIANTS */}
        <Form.List name={'variants'}
         
        >
          {(fields, {add, remove}) => (
            <>
              {fields.map(({key, name}) => (
                <div key={key} className={'border rounded p-4 mb-4'}>
                  <Form.Item
                    getValueFromEvent={e => e.target.value.toUpperCase()} 
                    name={[name, 'color']}
                    label={'Color'}
                    rules={[{required: true}]}
                  >
                    <Input />
                  </Form.Item>

                  {/* IMAGES  */}
                  <Form.Item
                    name={[name, 'images']}
                    label={'Images'}
                    valuePropName={'fileList'}
                    getValueFromEvent={normFile}
                  >
                    <ImgCrop rotationSlider>
                      <Upload
                        listType={'picture-card'}
                        multiple
                        beforeUpload={() => false} 
                        accept={'image/*'}
                        onChange={({fileList}) =>
                          form.setFieldValue(['variants', name, 'images'], fileList)
                        } 
                      >
                        <PlusOutlined />
                      </Upload>
                    </ImgCrop>
                  </Form.Item>

                  {/* SIZES */}
                  <Form.List name={[name, 'sizes']}>
                    {(sizeFields, {add: addSize, remove: removeSize}) => (
                      <>
                        {sizeFields.map(({key: sKey, name: sName}) => (
                          <div key={sKey} className={'flex md:flex-row flex-col md:gap-2 md:items-center border border-primary-100 rounded-md p-4  mb-4'}>
                            <Form.Item className={'flex mb-0 md:mb-5'} name={[sName, 'size']} label={'Size'}>
                              <Input />
                            </Form.Item>

                            <Form.Item className={'flex mb-0 md:mb-5'} name={[sName, 'extraPrice']} label={'Extra Price'}>
                              <InputNumber min={0} />
                            </Form.Item>

                            <Form.Item
                              className={'flex mb-0 md:mb-5'}
                              name={[sName, 'quantity']}
                              label={'Quantity'}
                              rules={[{required: true}]}
                            >
                              <InputNumber min={1} />
                            </Form.Item>

                            {sizeFields.length > 1 && (
                              <Button className={'w-12 md:mt-2'} danger onClick={() => removeSize(sName)}>
                                <DeleteOutlined />
                              </Button>
                            )}
                          </div>
                        ))}

                        <Button
                          type={'dashed'}
                          className={'text-primary-800font-medium'}
                          block
                          onClick={() => addSize({size: '', quantity: 1, extraPrice: 0})}
                        >
                          + Add Size
                        </Button>
                      </>
                    )}
                  </Form.List>

                  {fields.length > 1 && (
                    <Button className={'mt-4'} danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                      Remove Variant
                    </Button>
                  )}
                </div>
              ))}

              <Button 
                className={'bg-primary-50 text-primary-800 font-medium text-base'}
                type={'dashed'} block onClick={() =>
                  add({
                    color: '',
                    isExisting: false,
                    images: [],
                    sizes: [{size: '', quantity: 1, extraPrice: 0}],
                  })
                }
              >
                + Add Color Variant
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

NewProductScreen.propTypes = {
  brandOptions: PropTypes.array.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  promoCodeOptions: PropTypes.array.isRequired,
  isModalVisible: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
};
 
export default NewProductScreen;
